import type { SpotDetail } from "@divespots/contracts";
import { CircleCheck, CloudAlert, CloudCheck, CloudDownload, TriangleAlert, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { ButtonHTMLAttributes, ReactNode, Ref, RefObject } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../components/Button.tsx";
import { formatBytes } from "../i18n/format.ts";
import { useLocale } from "../i18n/index.ts";
import { SaveError, estimateFor, removeSpot, saveSpot } from "./saver.ts";
import type { SaveProgress } from "./saver.ts";
import { idbStore } from "./store.ts";
import { offlineSupported, useSavedSpots } from "./useSavedSpots.ts";

type State =
  | { kind: "idle" }
  | { kind: "confirm" }
  | { kind: "remove" }
  | { kind: "saving"; progress: SaveProgress }
  | { kind: "error"; error: "quota" | "network" | "failed" };

async function persistStorage(): Promise<void> {
  try {
    await navigator.storage?.persist?.();
  } catch {
    /* a refusal is fine: the data is just evictable under pressure */
  }
}

/** Does the browser have room for `bytes` more? Unknown counts as yes. */
async function hasRoom(bytes: number): Promise<boolean> {
  try {
    const { quota, usage } = (await navigator.storage?.estimate?.()) ?? {};
    return quota === undefined || usage === undefined || quota - usage > bytes * 1.1;
  } catch {
    return true;
  }
}

export interface OfflineSave {
  spot: SpotDetail;
  state: State;
  /** The saved copy of this spot, if any. */
  saved: ReturnType<typeof useSavedSpots>["spots"][number] | undefined;
  estimateBytes: number;
  percent: number;
  /** Screen-reader announcement for the last transition (started, saved); the percentage never lands here. */
  announce: "started" | "saved" | null;
  buttonRef: RefObject<HTMLButtonElement | null>;
  openConfirm: () => void;
  closeConfirm: () => void;
  openRemove: () => void;
  /** Closes the remove confirm and puts focus back on the row button. */
  closeRemove: () => void;
  remove: () => void;
  start: () => Promise<void>;
  cancel: () => void;
}

/** State of "Offline speichern" for one spot; `null` where the browser cannot store it. */
export function useOfflineSave(spot: SpotDetail): OfflineSave | null {
  const { spots } = useSavedSpots();
  const saved = spots.find((s) => s.slug === spot.slug);
  const [state, setState] = useState<State>({ kind: "idle" });
  const [announce, setAnnounce] = useState<"started" | "saved" | null>(null);
  const abort = useRef<AbortController | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => () => abort.current?.abort(), []);

  const estimateBytes = estimateFor(spot).bytes;

  const start = async () => {
    if (!(await hasRoom(estimateBytes))) return setState({ kind: "error", error: "quota" });
    const ctrl = new AbortController();
    abort.current = ctrl;
    setState({ kind: "saving", progress: { done: 0, total: 1, bytes: 0 } });
    setAnnounce("started");
    try {
      await saveSpot(
        spot,
        { store: idbStore, beforeWrite: persistStorage },
        ctrl.signal,
        (progress) => setState({ kind: "saving", progress }),
      );
      setState({ kind: "idle" });
      setAnnounce("saved");
    } catch (e) {
      setAnnounce(null);
      if (ctrl.signal.aborted) return setState({ kind: "idle" });
      setState({ kind: "error", error: e instanceof SaveError ? e.kind : "failed" });
    } finally {
      abort.current = null;
    }
  };

  if (!offlineSupported()) return null;
  const percent =
    state.kind === "saving"
      ? Math.min(100, Math.round((state.progress.done / Math.max(1, state.progress.total)) * 100))
      : 0;
  return {
    spot,
    state,
    saved,
    estimateBytes,
    percent,
    announce,
    buttonRef,
    openConfirm: () => {
      setAnnounce(null);
      setState({ kind: "confirm" });
    },
    closeConfirm: () => setState({ kind: "idle" }),
    openRemove: () => {
      setAnnounce(null);
      setState({ kind: "remove" });
    },
    closeRemove: () => {
      setState({ kind: "idle" });
      buttonRef.current?.focus();
    },
    remove: () => {
      setState({ kind: "idle" });
      void removeSpot(spot.slug, { store: idbStore });
      buttonRef.current?.focus();
    },
    start,
    cancel: () => abort.current?.abort(),
  };
}

/** Tinted 44px icon button, like its neighbours in the row (`IconButton` has no tinted tone). */
function ActionButton({
  icon,
  buttonRef,
  ...rest
}: {
  icon: ReactNode;
  "aria-label": string;
  buttonRef?: Ref<HTMLButtonElement>;
} & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button ref={buttonRef} type="button" className="ds-btn ds-btn--tinted ds-btn--icon" {...rest}>
      {icon}
    </button>
  );
}

/** A ring that fills with the percentage; the cancel cross sits inside it. */
function ProgressRing({ percent }: { percent: number }) {
  const r = 9;
  const c = 2 * Math.PI * r;
  return (
    <span className="offline-ring" aria-hidden="true">
      <svg viewBox="0 0 24 24">
        <circle className="offline-ring__track" cx="12" cy="12" r={r} />
        <circle
          className="offline-ring__bar"
          cx="12"
          cy="12"
          r={r}
          strokeDasharray={c}
          strokeDashoffset={c * (1 - percent / 100)}
          transform="rotate(-90 12 12)"
        />
      </svg>
      <X className="offline-ring__x" size={10} strokeWidth={2.5} />
    </span>
  );
}

/**
 * The icon-only offline button of the spot actions row. One icon per state, never colour alone:
 * cloud-download (not saved / confirming), a ring with a cross (saving, cancels), cloud-check
 * (saved, removes), cloud-alert (failed, retries). Text lives in `OfflineSaveStatus`.
 */
export function OfflineSaveButton({ model }: { model: OfflineSave }) {
  const { t } = useTranslation();
  const { state, saved, spot } = model;
  if (state.kind === "saving") {
    const label = t("offline.cancelSave");
    return (
      <ActionButton
        buttonRef={model.buttonRef}
        aria-label={label}
        title={label}
        icon={<ProgressRing percent={model.percent} />}
        onClick={model.cancel}
      />
    );
  }
  if (saved && state.kind !== "error") {
    const label = t("offline.removeNamed", { name: spot.name });
    return (
      <ActionButton
        buttonRef={model.buttonRef}
        aria-label={label}
        title={label}
        aria-expanded={state.kind === "remove"}
        icon={<CloudCheck aria-hidden="true" strokeWidth={1.75} />}
        onClick={state.kind === "remove" ? model.closeRemove : model.openRemove}
      />
    );
  }
  const confirming = state.kind === "confirm";
  const Icon = state.kind === "error" ? CloudAlert : CloudDownload;
  return (
    <ActionButton
      buttonRef={model.buttonRef}
      aria-label={t("offline.save")}
      title={t("offline.save")}
      aria-expanded={confirming}
      icon={<Icon aria-hidden="true" strokeWidth={1.75} />}
      onClick={confirming ? model.closeConfirm : model.openConfirm}
    />
  );
}

/** Everything the offline control says in words, below the row: progress, saved size, estimate, errors. */
export function OfflineSaveStatus({ model }: { model: OfflineSave }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const { state, saved } = model;
  let body: ReactNode = null;
  if (state.kind === "saving") {
    body = (
      <div className="offline-card">
        <div className="offline-card__row">
          <span className="ds-text-subheadline">
            {t("offline.saving", { percent: model.percent })}
          </span>
          <Button variant="tinted" onClick={model.cancel}>
            {t("offline.cancel")}
          </Button>
        </div>
        <div
          role="progressbar"
          aria-label={t("offline.progress")}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={model.percent}
          className="offline-progress"
        >
          <span style={{ width: `${model.percent}%` }} />
        </div>
      </div>
    );
  } else if (saved && state.kind === "remove") {
    body = <RemoveConfirm model={model} />;
  } else if (saved && state.kind !== "error") {
    body = (
      <p className="ds-text-subheadline offline-card__ok m-0">
        <CircleCheck aria-hidden="true" size={20} strokeWidth={1.75} />
        {t("offline.saved")} · {formatBytes(saved.bytes, locale)}
      </p>
    );
  } else if (state.kind === "confirm") {
    body = (
      <div className="offline-card">
        <p className="ds-text-subheadline m-0">
          {t("offline.estimate", { size: formatBytes(model.estimateBytes, locale) })}
        </p>
        <div className="offline-card__row">
          <Button variant="filled" onClick={() => void model.start()}>
            {t("offline.confirm")}
          </Button>
          <Button variant="tinted" onClick={model.closeConfirm}>
            {t("offline.cancel")}
          </Button>
        </div>
      </div>
    );
  } else if (state.kind === "error") {
    body = (
      <p className="ds-text-footnote offline-card__err m-0" role="alert">
        <TriangleAlert aria-hidden="true" size={16} strokeWidth={1.75} />
        {t(`offline.error.${state.error}`)}
      </p>
    );
  }
  return (
    <>
      {/* Only transitions are announced; the changing percentage lives on the progressbar. */}
      <div aria-live="polite" className="sr-only">
        {model.announce
          ? t(model.announce === "started" ? "offline.started" : "offline.saved")
          : null}
      </div>
      {body}
    </>
  );
}

/** "Remove the offline copy?" with Entfernen / Abbrechen; takes focus, Escape closes. */
function RemoveConfirm({ model }: { model: OfflineSave }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => ref.current?.focus(), []);
  return (
    <div
      ref={ref}
      tabIndex={-1}
      role="group"
      aria-label={t("offline.removeConfirm")}
      className="offline-card"
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.stopPropagation();
          model.closeRemove();
        }
      }}
    >
      <p className="ds-text-subheadline m-0">{t("offline.removeConfirm")}</p>
      <div className="offline-card__row">
        <Button variant="filled" onClick={model.remove}>
          {t("offline.remove")}
        </Button>
        <Button variant="tinted" onClick={model.closeRemove}>
          {t("offline.cancel")}
        </Button>
      </div>
    </div>
  );
}
